/**
 * F1 — Feature-flag context
 *
 * Egyszer tölti be a tenant aktív feature-öit az alkalmazás indulásakor
 * (bejelentkezés után). Gyerek-komponensek a useFeature() hookkal kérdezhetik le.
 *
 * Downgrade / zárolás esetén a backend dönt véglegesen — ez a kontextus
 * optimista UI-megjelenítésre szolgál (pl. menüpontok, CTA szövegek).
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { get } from '../lib/api';

export type FeatureName =
  | 'szamlazo' | 'hazipenztar' | 'cikkek_1arlista' | 'partnerek' | 'attekintes'
  | 'ajanlat' | 'tobb_arlista' | 'alap_riport'
  | 'projektek' | 'csapat' | 'teljes_riport'
  | 'raktar' | 'kintlevoseg' | 'banki_parosito'
  | 'konfigurator_basic' | 'konfigurator_pro'
  | 'idopont_basic' | 'idopont_pro';

interface FeatureState {
  activeFeatures: FeatureName[];
  planCode: string;
  status: string;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  discount15Offered: boolean;
  discount15Applied: boolean;
  warning28Shown: boolean;
  dataPurgeAt: string | null;
  loading: boolean;
  /** Csak belső használatra: manualisan frissíti a feature-listát (pl. csomag-váltás után) */
  refresh: () => void;
}

const FeatureContext = createContext<FeatureState | null>(null);

export function FeatureProvider({ children }: { children: ReactNode }) {
  const [activeFeatures, setActiveFeatures] = useState<FeatureName[]>([]);
  const [planCode, setPlanCode] = useState('');
  const [status, setStatus] = useState('');
  const [trialStartedAt, setTrialStartedAt] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [discount15Offered, setDiscount15Offered] = useState(false);
  const [discount15Applied, setDiscount15Applied] = useState(false);
  const [warning28Shown, setWarning28Shown] = useState(false);
  const [dataPurgeAt, setDataPurgeAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // Ha nincs token, ne hívjuk az API-t (pl. bejelentkezési oldal)
    if (!localStorage.getItem('accessToken')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await get<{
        activeFeatures: FeatureName[];
        planCode: string;
        status: string;
        trialStartedAt: string | null;
        trialEndsAt: string | null;
        discount15Offered: boolean;
        discount15Applied: boolean;
        warning28Shown: boolean;
        dataPurgeAt: string | null;
      }>('/features');
      setActiveFeatures(data.activeFeatures ?? []);
      setPlanCode(data.planCode ?? '');
      setStatus(data.status ?? '');
      setTrialStartedAt(data.trialStartedAt ?? null);
      setTrialEndsAt(data.trialEndsAt ?? null);
      setDiscount15Offered(data.discount15Offered ?? false);
      setDiscount15Applied(data.discount15Applied ?? false);
      setWarning28Shown(data.warning28Shown ?? false);
      setDataPurgeAt(data.dataPurgeAt ?? null);
    } catch {
      // Ha nem sikerül (pl. nincs subscription) → minden aktív (fejlesztői fallback)
      setActiveFeatures([
        'szamlazo', 'hazipenztar', 'cikkek_1arlista', 'partnerek', 'attekintes',
        'ajanlat', 'tobb_arlista', 'alap_riport',
        'projektek', 'csapat', 'teljes_riport',
        'raktar', 'kintlevoseg', 'banki_parosito',
        'konfigurator_basic', 'konfigurator_pro',
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <FeatureContext.Provider value={{
      activeFeatures, planCode, status,
      trialStartedAt, trialEndsAt,
      discount15Offered, discount15Applied, warning28Shown, dataPurgeAt,
      loading, refresh: load,
    }}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatureContext(): FeatureState {
  const ctx = useContext(FeatureContext);
  if (!ctx) throw new Error('useFeatureContext must be used inside FeatureProvider');
  return ctx;
}
